import { For, Show, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { A } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { TaskFailureCapsuleDto, TaskFailureCapsuleStatusDto, VektorMonitorBotDto } from '../api';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { Badge } from '../components/Badge';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { formatBytes, formatInteger } from '../services/numberFormat';
import { firstError } from '../api/envelope';

const PAGE_SIZE = 25;

const CAPTURE_ERROR_KEYS: Record<string, string> = {
    capsules_disabled: 'TASK_CAPSULE_CAPTURE_DISABLED',
    storage_unavailable: 'TASK_CAPSULE_STATUS_NO_STORAGE',
    bot_not_buffered: 'TASK_CAPSULE_CAPTURE_BOT_OFFLINE',
};

function taskLabel(capsule: TaskFailureCapsuleDto): string {
    return capsule.isSubtask
        ? `${capsule.rootTaskName} / ${capsule.failedTaskName}`
        : capsule.failedTaskName ?? '';
}

export default function AdminTaskCapsules() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [bots, setBots] = createSignal<VektorMonitorBotDto[]>([]);
    const [botFilter, setBotFilter] = createSignal('');
    const [search, setSearch] = createSignal('');
    const [page, setPage] = createSignal(0);
    const [status, setStatus] = createSignal<TaskFailureCapsuleStatusDto | null>(null);
    const [openCapsuleId, setOpenCapsuleId] = createSignal<string | null>(null);
    const [busy, setBusy] = createSignal<string | null>(null);
    const [captureBotId, setCaptureBotId] = createSignal('');
    const [capturing, setCapturing] = createSignal(false);

    const [capsules, { refetch }] = createResource(
        () => ({ botId: botFilter(), search: search().trim(), page: page() }),
        async (params) => {
            const res = await api.taskFailureCapsuleAdmin.list({
                botId: params.botId || undefined,
                search: params.search || undefined,
                page: params.page,
                pageSize: PAGE_SIZE,
            });
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('TASK_CAPSULE_LOAD_FAILED'));
                return { capsules: [], total: 0 };
            }
            return { capsules: res.data.value.capsules ?? [], total: res.data.value.total ?? 0 };
        });

    const [detail] = createResource(openCapsuleId, async (capsuleId) => {
        const res = await api.taskFailureCapsuleAdmin.get(capsuleId);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('TASK_CAPSULE_LOAD_FAILED'));
            return null;
        }
        return res.data.value;
    });

    const totalPages = () => Math.ceil((capsules()?.total ?? 0) / PAGE_SIZE);

    async function fetchStatus() {
        try {
            const res = await api.taskFailureCapsuleAdmin.status();
            if (isOk(res.data)) setStatus(res.data.value);
        } catch { /* the poll retries; a toast on every tick would be noise */ }
    }

    async function fetchBots() {
        try {
            const res = await api.packetRecordingAdmin.listRecordableBots();
            if (isOk(res.data)) setBots(res.data.value.bots ?? []);
        } catch { /* the bot filter stays at "all bots" */ }
    }

    async function deleteCapsule(capsule: TaskFailureCapsuleDto) {
        const confirmed = await showConfirm(
            t('TASK_CAPSULE_DELETE_CONFIRM', capsule.botUsername ?? '', formatBytes(capsule.compressedBytes ?? 0)),
            true);
        if (!confirmed) return;
        setBusy(capsule.capsuleId!);
        try {
            const res = await api.taskFailureCapsuleAdmin.delete(capsule.capsuleId!);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('TASK_CAPSULE_DELETE_FAILED'));
                return;
            }
            toast?.info(t('TASK_CAPSULE_DELETED_TOAST', capsule.botUsername ?? ''));
            if (openCapsuleId() === capsule.capsuleId) setOpenCapsuleId(null);
            await refetch();
        } catch (e: any) {
            toast?.error(e?.message ?? t('TASK_CAPSULE_DELETE_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function captureNow() {
        const botId = captureBotId();
        if (!botId) return;
        const botName = bots().find(bot => bot.botId === botId)?.username ?? '';
        setCapturing(true);
        try {
            const res = await api.taskFailureCapsuleAdmin.capture(botId);
            if (!isOk(res.data)) {
                const errorKey = CAPTURE_ERROR_KEYS[firstError(res.data) ?? ''] ?? 'TASK_CAPSULE_CAPTURE_FAILED';
                toast?.error(t(errorKey, botName));
                return;
            }
            toast?.info(t('TASK_CAPSULE_CAPTURED_TOAST', res.data.value.botUsername ?? botName));
            setPage(0);
            await refetch();
        } catch (e: any) {
            toast?.error(e?.message ?? t('TASK_CAPSULE_CAPTURE_FAILED'));
        } finally {
            setCapturing(false);
        }
    }

    function downloadUrl(capsule: TaskFailureCapsuleDto): string {
        return `/api/vektor/admin/task-capsules/${capsule.capsuleId}/download`;
    }

    function downloadName(capsule: TaskFailureCapsuleDto): string {
        const capturedAt = (capsule.capturedAtUtc ?? '').replace(/[:.]/g, '-').replace(/T/, '_').slice(0, 19);
        return `task-capsule-${capsule.botUsername}-${capsule.isManual ? 'manual' : capsule.failedTaskName}-${capturedAt}.zip`;
    }

    function updateFilter<T>(setter: (value: T) => void, value: T) {
        setter(value);
        setPage(0);
    }

    onMount(() => {
        void fetchBots();
        void fetchStatus();
        const refresh = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchStatus();
        }, 10000);
        onCleanup(() => clearInterval(refresh));
    });

    const DownloadLink = (props: { capsule: TaskFailureCapsuleDto }) => (
        // download and rel=external both stop the router turning the click into a client-side route.
        <a class="btn btn-secondary btn-sm" href={downloadUrl(props.capsule)} download={downloadName(props.capsule)} rel="external">
            <i class="fa-solid fa-download" /> <Text val="TASK_CAPSULE_DOWNLOAD" />
        </a>
    );

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="TASK_CAPSULE_TITLE" /></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    <Text val="TASK_CAPSULE_SUBTITLE" />
                </span>
            </div>

            <Show when={status()}>{(current) =>
                <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                    <Show
                        when={current().enabled}
                        fallback={<>
                            <Badge tone="neutral" icon="fa-power-off" pill><Text val="TASK_CAPSULE_STATUS_OFF" /></Badge>
                            <span style={{ flex: '1', 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                <Text val="TASK_CAPSULE_STATUS_OFF_HINT" />
                            </span>
                            <A class="btn btn-secondary btn-sm" href="/admin/feature-flags">
                                <i class="fa-solid fa-flag" /> <Text val="TASK_CAPSULE_OPEN_FLAGS" />
                            </A>
                        </>}
                    >
                        <Show
                            when={current().storageAvailable}
                            fallback={<Badge tone="error" icon="fa-triangle-exclamation" pill><Text val="TASK_CAPSULE_STATUS_NO_STORAGE" /></Badge>}
                        >
                            <Badge tone="success" icon="fa-circle-dot" pill pulse><Text val="TASK_CAPSULE_STATUS_ON" /></Badge>
                        </Show>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                            {t('TASK_CAPSULE_STATUS_BUFFERED', formatInteger(current().bufferedBots ?? 0), formatInteger(current().bufferedRecords ?? 0))}
                            <Show when={(current().capturesInFlight ?? 0) > 0}>
                                {' · '}{t('TASK_CAPSULE_STATUS_WRITING', formatInteger(current().capturesInFlight ?? 0))}
                            </Show>
                        </span>
                        <Show when={current().storageAvailable}>
                            <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center', 'flex-wrap': 'wrap', 'margin-left': 'auto' }}>
                                <select
                                    value={captureBotId()}
                                    onInput={(e) => setCaptureBotId(e.currentTarget.value)}
                                    aria-label={t('TASK_CAPSULE_CAPTURE_BOT')}
                                >
                                    <option value="">{t('TASK_CAPSULE_CAPTURE_PICK_BOT')}</option>
                                    <For each={bots()}>{(bot) => <option value={bot.botId}>{bot.username}</option>}</For>
                                </select>
                                <button
                                    class="btn btn-primary btn-sm"
                                    disabled={!captureBotId() || capturing()}
                                    onClick={() => void captureNow()}
                                >
                                    <i class={`fa-solid ${capturing() ? 'fa-spinner fa-spin' : 'fa-camera'}`} /> <Text val="TASK_CAPSULE_CAPTURE" />
                                </button>
                            </div>
                        </Show>
                    </Show>
                </div>
            }</Show>

            <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1 1 14rem' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="TASK_CAPSULE_FILTER_BOT" />
                    </span>
                    <select value={botFilter()} onInput={(e) => updateFilter(setBotFilter, e.currentTarget.value)}>
                        <option value="">{t('TASK_CAPSULE_FILTER_ALL_BOTS')}</option>
                        <For each={bots()}>{(bot) => <option value={bot.botId}>{bot.username}</option>}</For>
                    </select>
                </label>
                <div style={{ flex: '2 1 14rem' }}>
                    <SearchInput
                        value={search()}
                        onInput={(next) => updateFilter(setSearch, next)}
                        placeholder={t('TASK_CAPSULE_SEARCH_PLACEHOLDER')}
                        ariaLabel={t('TASK_CAPSULE_SEARCH_PLACEHOLDER')}
                    />
                </div>
                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                    {t('TASK_CAPSULE_COUNT', formatInteger(capsules()?.total ?? 0))}
                </span>
            </div>

            <Show
                when={capsules.state !== 'pending'}
                fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonAuditRow /><SkeletonAuditRow /><SkeletonAuditRow /></div>}
            >
                <Show
                    when={(capsules()?.capsules.length ?? 0) > 0}
                    fallback={<EmptyState
                        icon="fa-box-archive"
                        title={t(search() || botFilter() ? 'TASK_CAPSULE_EMPTY_FILTERED' : 'TASK_CAPSULE_EMPTY')}
                        hint={search() || botFilter() ? undefined : t('TASK_CAPSULE_EMPTY_HINT')}
                    />}
                >
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <For each={capsules()?.capsules}>{(capsule) =>
                            <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)', 'flex-wrap': 'wrap' }}>
                                <i
                                    class={`fa-solid ${capsule.isManual ? 'fa-camera' : 'fa-box-archive'}`}
                                    style={{ color: capsule.isManual ? 'var(--primary-500)' : 'var(--error-500)', 'font-size': 'var(--font-size-lg)' }}
                                />
                                <div class="v" style={{ flex: '1 1 14rem', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                    <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center', 'flex-wrap': 'wrap' }}>
                                        <span style={{ 'font-weight': 'var(--font-weight-semibold)' }}>{taskLabel(capsule) || t('TASK_CAPSULE_MANUAL_IDLE')}</span>
                                        <Show when={capsule.isManual}>
                                            <Badge tone="info" icon="fa-camera"><Text val="TASK_CAPSULE_MANUAL" /></Badge>
                                        </Show>
                                        <Show when={capsule.isSubtask}>
                                            <Badge tone="info"><Text val="TASK_CAPSULE_SUBTASK" /></Badge>
                                        </Show>
                                        <Show when={(capsule.additionalFailureCount ?? 0) > 0}>
                                            <Badge tone="warning">{t('TASK_CAPSULE_MORE_FAILURES', String(capsule.additionalFailureCount))}</Badge>
                                        </Show>
                                        <Show when={(capsule.suppressedCount ?? 0) > 0}>
                                            <Badge tone="neutral">{t('TASK_CAPSULE_SUPPRESSED', String(capsule.suppressedCount))}</Badge>
                                        </Show>
                                    </div>
                                    <div style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-secondary)', 'overflow-wrap': 'anywhere' }}>
                                        {capsule.error ?? t(capsule.isManual ? 'TASK_CAPSULE_MANUAL_NO_ERROR' : 'TASK_CAPSULE_NO_ERROR')}
                                    </div>
                                    <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                        {capsule.botUsername} · {formatDateTime(capsule.capturedAtUtc!)} · {formatBytes(capsule.compressedBytes ?? 0)}
                                        {' · '}{t('TASK_CAPSULE_RECORDS', formatInteger(capsule.recordCount ?? 0))}
                                    </div>
                                </div>
                                <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                                    <button class="btn btn-secondary btn-sm" onClick={() => setOpenCapsuleId(capsule.capsuleId!)}>
                                        <i class="fa-solid fa-magnifying-glass" /> <Text val="TASK_CAPSULE_DETAILS" />
                                    </button>
                                    <DownloadLink capsule={capsule} />
                                    <button
                                        class="btn btn-danger btn-sm"
                                        disabled={busy() === capsule.capsuleId}
                                        onClick={() => void deleteCapsule(capsule)}
                                    >
                                        <i class="fa-solid fa-trash" /> <Text val="TASK_CAPSULE_DELETE" />
                                    </button>
                                </div>
                            </div>
                        }</For>
                    </div>
                    <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
                </Show>
            </Show>

            <Modal
                open={openCapsuleId() !== null}
                onClose={() => setOpenCapsuleId(null)}
                title={<Text val={detail()?.capsule?.isManual ? 'TASK_CAPSULE_DETAIL_TITLE_MANUAL' : 'TASK_CAPSULE_DETAIL_TITLE'} />}
                maxWidth="56rem"
            >
                <Show
                    when={!detail.loading}
                    fallback={<div class="h" style={{ 'justify-content': 'center', padding: 'var(--spacing-xl)' }}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
                >
                    <Show when={detail()} fallback={<EmptyState icon="fa-box-archive" title={t('TASK_CAPSULE_DETAIL_MISSING')} />}>{(loaded) =>
                        <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span style={{ 'font-weight': 'var(--font-weight-semibold)' }}>{taskLabel(loaded().capsule!) || t('TASK_CAPSULE_MANUAL_IDLE')}</span>
                                <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                    {loaded().capsule!.botUsername}
                                    {' · '}{t('TASK_CAPSULE_WINDOW', formatDateTime(loaded().capsule!.windowStartUtc!), formatDateTime(loaded().capsule!.capturedAtUtc!))}
                                    {' · '}{t('TASK_CAPSULE_RECORDS', formatInteger(loaded().capsule!.recordCount ?? 0))}
                                    {' · '}{t('TASK_CAPSULE_WORLD_FRAMES', formatInteger(loaded().capsule!.worldFrameCount ?? 0))}
                                </span>
                            </div>

                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="TASK_CAPSULE_ERROR" /></span>
                                <span style={{ 'overflow-wrap': 'anywhere' }}>{loaded().capsule!.error ?? t(loaded().capsule!.isManual ? 'TASK_CAPSULE_MANUAL_NO_ERROR' : 'TASK_CAPSULE_NO_ERROR')}</span>
                            </div>

                            <Show when={!loaded().capsule!.isManual}>
                                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="TASK_CAPSULE_STACK_TRACE" /></span>
                                    <Show
                                        when={loaded().exceptionDetail}
                                        fallback={<span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="TASK_CAPSULE_NO_STACK_TRACE" /></span>}
                                    >
                                        <pre class="commandtext" style={{ margin: 0, 'max-height': '18rem', overflow: 'auto', 'white-space': 'pre', 'font-size': 'var(--font-size-xs)' }}>
                                            {loaded().exceptionDetail}
                                        </pre>
                                    </Show>
                                </div>
                            </Show>

                            <Show when={(loaded().additionalFailures?.length ?? 0) > 0}>
                                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="TASK_CAPSULE_ADDITIONAL_FAILURES" /></span>
                                    <For each={loaded().additionalFailures}>{(failure) =>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)', padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--dark-bg-tertiary)', 'border-radius': 'var(--radius-md)' }}>
                                            <span style={{ 'font-weight': 'var(--font-weight-medium)' }}>
                                                {failure.pluginName}/{failure.taskName} · {formatDateTime(failure.atUtc!)}
                                            </span>
                                            <span style={{ 'font-size': 'var(--font-size-sm)', 'overflow-wrap': 'anywhere' }}>{failure.error ?? t('TASK_CAPSULE_NO_ERROR')}</span>
                                        </div>
                                    }</For>
                                </div>
                            </Show>

                            <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}><Text val="TASK_CAPSULE_ZIP_CONTENTS" /></span>

                            <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                                <button class="btn btn-danger btn-sm" disabled={busy() === loaded().capsule!.capsuleId} onClick={() => void deleteCapsule(loaded().capsule!)}>
                                    <i class="fa-solid fa-trash" /> <Text val="TASK_CAPSULE_DELETE" />
                                </button>
                                <DownloadLink capsule={loaded().capsule!} />
                            </div>
                        </div>
                    }</Show>
                </Show>
            </Modal>
        </div>
    );
}
